import { gridCells, gridTotal } from '@signa/shared';
/** 潰れた入力に返す値。**呼び出し側は寸法 0 を見て描画を諦める。** */
const EMPTY = { x: 0, y: 0, width: 0, height: 0 };
/** 使える寸法か。**メタデータ待ちの `<video>` は `videoWidth === 0`。** */
function usable(value) {
    return Number.isFinite(value) && value > 0;
}
/** 2 点の距離。 */
function distance(a, b) {
    if (a === undefined || b === undefined)
        return 0;
    return Math.hypot(b[0] - a[0], b[1] - a[1]);
}
/**
 * クアッドの辺の長さから見た寸法。
 *
 * **頂点は左上から時計回り**(`[左上, 右上, 右下, 左下]` —— F-08 / #28)。
 * 上下の辺の平均を幅、左右の辺の平均を高さとする。
 */
function edgeSize(quad) {
    const [tl, tr, br, bl] = quad;
    return {
        width: (distance(tl, tr) + distance(bl, br)) / 2,
        height: (distance(tl, bl) + distance(tr, br)) / 2,
    };
}
/**
 * クアッドの**辺の長さの比**(横 ÷ 縦)。
 *
 * **これは推定ではない。** 4 点から元の長方形の比率を復元する計算は
 * 主点と焦点距離の仮定が必要で、**クアッドが平行四辺形に近いと不定**になる ——
 * [ADR-0028](../../../docs/adr/0028-video-fit-and-panel-aspect.md) はその方式を
 * 採らないと決めた。ここで返すのは**画面に見えているとおりの比**である。
 *
 * 盤面の実比率が入力されていないあいだの既定として使う。**入力が無くても
 * 引き伸ばしは止まる**のが要点。
 *
 * 測れないときは `0`。
 */
export function apparentAspect(quad) {
    const { width, height } = edgeSize(quad);
    if (!usable(width) || !usable(height))
        return 0;
    return width / height;
}
/**
 * 盤面の矩形の寸法。ホモグラフィの**写し元**になる。
 *
 * `aspect` を渡すとその比率の長方形にする(#148 で入力できるようになる)。
 * 渡さなければ [`apparentAspect`](#apparentAspect) に落とす。
 *
 * **絶対の大きさに意味は無い**(ホモグラフィが吸収する)。それでも辺の長さを
 * 基準にするのは、**値を表示ピクセルの桁に収めて浮動小数の精度を保つ**ため。
 *
 * 測れないときは寸法 0 を返す。**呼び出し側は描画を諦める** ——
 * 潰れたクアッドは `rectToQuad` が `DegenerateQuadError` を投げる経路に任せる。
 */
export function panelSize(quad, aspect) {
    const edges = edgeSize(quad);
    if (!usable(edges.height))
        return { width: 0, height: 0 };
    const ratio = aspect != null && usable(aspect.width) && usable(aspect.height)
        ? aspect.width / aspect.height
        : apparentAspect(quad);
    if (!usable(ratio))
        return { width: 0, height: 0 };
    return { width: edges.height * ratio, height: edges.height };
}
/**
 * 盤面の中で素材が占める矩形。**縦横比を保って中央に収める**(`contain`)。
 *
 * 余る側に帯ができる。**切らないのが要点** ——
 * プレビューの目的は「素材がどう見えるか」の確認で、
 * **端が切れて見えないほうが困る**([ADR-0028](../../../docs/adr/0028-video-fit-and-panel-aspect.md))。
 *
 * どちらかの寸法が使えなければ寸法 0(呼び出し側は描画を諦める)。
 */
export function containRect(content, panel) {
    if (!usable(content.width) || !usable(content.height))
        return EMPTY;
    if (!usable(panel.width) || !usable(panel.height))
        return EMPTY;
    // **小さいほうの倍率を採る。** これで必ず盤面の内側に収まる。
    const scale = Math.min(panel.width / content.width, panel.height / content.height);
    const width = content.width * scale;
    const height = content.height * scale;
    return {
        x: (panel.width - width) / 2,
        y: (panel.height - height) / 2,
        width,
        height,
    };
}
/**
 * 盤面を埋める矩形。**縦横比を保ち、中央を基準にはみ出させる**(`cover` —— #326 /
 * [ADR-0056](../../../docs/adr/0056-frame-video-fit.md))。
 *
 * [`containRect`](#containRect) と対になる。違うのは倍率の採り方だけで、
 * 返る矩形は**盤面を必ず包む**(`x` / `y` は 0 以下になる)。はみ出した分は
 * 描画側がセルの範囲しか塗らないことで切れる。
 *
 * どちらかの寸法が使えなければ寸法 0(呼び出し側は描画を諦める)。
 */
export function coverRect(content, panel) {
    if (!usable(content.width) || !usable(content.height))
        return EMPTY;
    if (!usable(panel.width) || !usable(panel.height))
        return EMPTY;
    // **大きいほうの倍率を採る。** これで必ず盤面を覆う。
    const scale = Math.max(panel.width / content.width, panel.height / content.height);
    const width = content.width * scale;
    const height = content.height * scale;
    return {
        x: (panel.width - width) / 2,
        y: (panel.height - height) / 2,
        width,
        height,
    };
}
/** 矩形からセルを作る。`corners` を正とし、`origin` / `size` はそこから導く。 */
function cellOf(index, left, top, right, bottom) {
    return {
        index,
        corners: [
            [left, top],
            [right, top],
            [right, bottom],
            [left, bottom],
        ],
        origin: [left, top],
        // **`right - left` で出す。** `left + width` を頂点に使うと誤差で境目がずれる。
        size: { width: right - left, height: bottom - top },
    };
}
/**
 * 枠の割り付け。**描画コアが GL に渡す値をここで決める。**
 *
 * **GL の呼び出しから判断を切り離すため**に置いている ——
 * [ADR-0019](../../../docs/adr/0019-dev-foundation.md) により WebGL と DOM 構築は
 * jsdom で試さないので、**判断がシェーダの呼び出しに埋まっていると検査できない。**
 *
 * `grid` を渡すと分割する(#152)。渡さなければセル 1 つ ——
 * 比率は [`apparentAspect`](#apparentAspect) に落ちる。
 *
 * 描けないときは `null`(クアッドが潰れている)。**呼び出し側は早期に戻る。**
 */
export function frameLayout(stageQuad, grid) {
    const panel = panelSize(stageQuad, grid == null ? null : gridTotal(grid));
    if (!usable(panel.width) || !usable(panel.height))
        return null;
    return { panel, cells: panelCells(panel, grid) };
}
/**
 * **展開図の寸法からセルを割り付ける。** [`frameLayout`](#frameLayout) の後半で、展開図の寸法を
 * クアッドから求めない枠(曲面 —— #361 / [ADR-0060](../../../docs/adr/0060-curved-surface.md))も同じものを使う。
 *
 * `grid` が無ければ展開図全体のセル 1 つ。寸法は使えるものを渡す(呼び出し側が確かめる)。
 */
export function panelCells(panel, grid) {
    const total = grid == null ? null : gridTotal(grid);
    if (grid == null || total == null) {
        return [cellOf(0, 0, 0, panel.width, panel.height)];
    }
    /*
     * 比率の単位を盤面座標に開く。**両側が同じ倍率を同じ境界値に掛ける**ので、
     * 隣り合うセルの境界は掛けたあとも厳密に一致する。
     */
    const scaleX = panel.width / total.width;
    const scaleY = panel.height / total.height;
    return gridCells(grid).map((cell) => cellOf(cell.index, cell.left * scaleX, cell.top * scaleY, cell.right * scaleX, cell.bottom * scaleY));
}
/**
 * セルの中で素材が占める矩形(**盤面座標**)。
 *
 * 枠の収め方(`fit`)に従ってセルに合わせ、セルの位置ぶんだけずらす。
 *
 * - `'cover'` —— [`coverRect`](#coverRect)。セルを覆い、はみ出しは描かれずに切れる
 * - **それ以外(`'contain'` / `null` / 省略)** —— [`containRect`](#containRect)。
 *   余った領域はシェーダが黒帯にする(ADR-0028)。**`fit` の無い既存の枠はここを通る** ——
 *   収め方を選べるようにする前とまったく同じ値になる(ADR-0056 の決定 2)
 *
 * 描けないときは `null` —— 動画のメタデータが揃っていない(`videoWidth === 0`)、
 * またはセルが潰れている。
 */
export function cellContent(cell, video, fit) {
    const inner = fit === 'cover' ? coverRect(video, cell.size) : containRect(video, cell.size);
    if (!usable(inner.width) || !usable(inner.height))
        return null;
    const [x, y] = cell.origin;
    return { x: x + inner.x, y: y + inner.y, width: inner.width, height: inner.height };
}
/**
 * **展開図全体**の中で素材が占める矩形(**盤面座標**)。折れた枠に使う(#339 / [ADR-0057](../../../docs/adr/0057-folded-frame.md))。
 *
 * 折れた枠は素材が**全体で 1 本**なので、セルではなく展開図全体に収める ——
 * 各面はその受け持ちの範囲を切り出して描く。収め方は [`cellContent`](#cellContent) と同じく `fit` に従う
 * (`cover` ならはみ出しは展開図の外で切れる、それ以外は余りが黒帯)。
 *
 * 描けないときは `null`(素材のメタデータが揃っていない・展開図が潰れている)。
 */
export function panelContent(panel, video, fit) {
    return cellContent(cellOf(0, 0, 0, panel.width, panel.height), video, fit);
}
//# sourceMappingURL=fit.js.map